import { useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { Can, useAuth } from '@gbs-monorepo-packages/auth';
import {
  HighUserRoles,
  type ISidebarHeaderProps,
  LogoutSidebarItem,
  SidebarHeader,
  SidebarHeaderMenuProfileItem,
  SidebarItem,
  getRouteFrom,
  removeSessionStorage,
} from '@gbs-monorepo-packages/common';

import { ADMIN_URL } from '../../constants/Env';
import { ALL_MEMBERS, DASHBOARD } from '../../constants/RoutePaths';
import { EdgeStorageKey } from '../../constants/Storage';
import { useClients } from '../../hooks/useCompanies';
import { useTermsGuard } from '../../hooks/useTermsGuard';
import { AdminPortalIcon, DashBoardIcon, MembersIcon } from './styles';

const DashboardRoute = getRouteFrom(DASHBOARD);
const MembersRoute = getRouteFrom(ALL_MEMBERS);

const titleTeammates = 'User Manager';
const titleCompanies = 'Clients';

interface IDefaultLayoutProps extends Omit<ISidebarHeaderProps, 'children'> {}

export const DefaultLayout = ({
  ...props
}: IDefaultLayoutProps): JSX.Element => {
  useTermsGuard();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { selectClient } = useClients();

  const handleSignOut = useCallback(async () => {
    removeSessionStorage(EdgeStorageKey.CLIENT);
    await signOut();
    selectClient(null);
  }, [signOut, selectClient]);

  return (
    <SidebarHeader
      data-cy="text-benefit-education"
      logoRedirectTo="/"
      logoText="Benefit Education"
      userName={user ? `${user?.firstName} ${user?.lastName}` : ''}
      imgPath={user?.accountFile?.path ?? ''}
      sidebarChildren={(isShrunken) => (
        <Can roles={HighUserRoles}>
          <SidebarItem
            dataCy={`navLink-clients`}
            icon={<DashBoardIcon />}
            isShrunken={isShrunken}
            title={titleCompanies}
            type="link"
            to={DashboardRoute}
          />
          <SidebarItem
            dataCy={`navLink-userManager`}
            icon={<MembersIcon />}
            isShrunken={isShrunken}
            title={titleTeammates}
            type="link"
            to={MembersRoute}
          />
        </Can>
      )}
      sidebarFooterChildren={(isShrunken) => (
        <>
          <Can roles={HighUserRoles}>
            <SidebarItem
              dataCy={`navLink-dashboardAdmin`}
              icon={<AdminPortalIcon />}
              isShrunken={isShrunken}
              title={'Admin Portal'}
              type="link"
              to={ADMIN_URL + '/redirect-login?userId=' + user?.id.toString()}
            />
          </Can>
          <LogoutSidebarItem
            isShrunken={isShrunken}
            handleSignOut={handleSignOut}
          />
        </>
      )}
      menuProfileChildren={
        <SidebarHeaderMenuProfileItem
          data-cy="option-settings"
          onClick={() => {
            if (!pathname.includes('profile')) {
              navigate('/profile', { state: { from: pathname } });
            }
          }}
        >
          Settings
        </SidebarHeaderMenuProfileItem>
      }
      {...props}
    >
      <Outlet />
    </SidebarHeader>
  );
};
